import { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Switch } from '@/components/ui-kit/switch';
import { Textarea } from '@/components/ui-kit/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';

import { useAuthStore } from '@/state/store/auth';
import { useGetToolDetails, useUpdateMCPServer } from '@/modules/gpt-chats/hooks/use-tools';
import { IAPIError, IUpdateMCPServerPayload } from '@/modules/gpt-chats/types/tools.service.type';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Button } from '@/components/ui-kit/button';
import { useTranslation } from 'react-i18next';

interface EditMcpServerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: {
    id: string;
    name: string;
    url: string;
    description: string;
    status?: string;
  };
  isBlocksTool: boolean;
  copiedBlocksTool: boolean;
}

interface EditMCPServerFormData {
  toolName: string;
  description: string;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  status: boolean;
}

export const EditMcpServerModal = ({
  open,
  onOpenChange,
  tool,
  isBlocksTool,
  copiedBlocksTool,
}: EditMcpServerModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.memberships?.some((m) => m?.roles?.includes('admin'));
  const { mutateAsync: updateMCPServer, isPending } = useUpdateMCPServer();

  const { data: toolDetails } = useGetToolDetails(tool?.id || '', isBlocksTool, open);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
    watch,
  } = useForm<EditMCPServerFormData>({
    defaultValues: {
      toolName: '',
      description: '',
      isOpenTool: false,
      isBlocksAuthenticated: false,
      status: false,
    },
  });

  useEffect(() => {
    if (open) {
      if (toolDetails) {
        reset(
          {
            toolName: toolDetails.Name,
            description: toolDetails.Description || '',
            isOpenTool: toolDetails.IsOpenTool || false,
            isBlocksAuthenticated: toolDetails.IsBlocksAuthenticated || false,
            status: toolDetails.Status === 'active',
          },
          { keepDirtyValues: true }
        );
      } else if (tool) {
        reset(
          {
            toolName: tool.name,
            description: tool.description,
            isOpenTool: isBlocksTool || false,
            isBlocksAuthenticated: false,
            status: tool.status === 'active',
          },
          { keepDirtyValues: true }
        );
      }
    }
  }, [open, toolDetails, tool, reset, isBlocksTool]);

  const isBlocksToolChecked = watch('isOpenTool');

  const onSubmit = async (data: EditMCPServerFormData) => {
    if (!tool?.id) {
      showErrorToast({ errors: t('TOOL_ID_NOT_FOUND') });
      return;
    }

    try {
      const payload: IUpdateMCPServerPayload = {
        tool_id: tool.id,
        name: data.toolName,
        description: data.description || '',
        is_open_tool: isAdmin ? data.isOpenTool : undefined,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : undefined,
        status: isAdmin && data.isOpenTool ? (data.status ? 'active' : 'inactive') : undefined,
      };
      const response = await updateMCPServer(payload);

      if (response.is_success) {
        showSuccessToast({ description: response.detail || t('MCP_SERVER_UPDATED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['tools'] });
        handleCancel();
      } else {
        showErrorToast({ errors: response.message || t('FAILED_TO_UPDATE_MCP_SERVER') });
      }
    } catch (error: unknown) {
      const apiError = error as IAPIError;
      if (apiError?.detail && Array.isArray(apiError.detail)) {
        const errorMessages = apiError.detail.map((err) => err.msg).join(', ');
        showErrorToast({ errors: errorMessages });
      } else {
        showErrorToast({ errors: ErrorTransformer(error) });
      }
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">{t('EDIT_MCP_SERVER')}</DialogTitle>
          <p className="mt-2 text-sm text-muted-foreground">{t('EDIT_TOOL_DESCRIPTION')}</p>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          <div className="flex gap-6">
            {/* <div className="flex w-[162px] flex-col items-center gap-3">
              <div className="flex h-[162px] w-full items-center justify-center rounded-lg bg-red-600">
                <svg
                  className="h-16 w-16 text-white"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M32 8C18.7 8 8 18.7 8 32C8 45.3 18.7 56 32 56C45.3 56 56 45.3 56 32C56 18.7 45.3 8 32 8Z"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M32 20C32 20 24 28 24 32C24 36 27.6 39.6 32 39.6C36.4 39.6 40 36 40 32C40 28 32 20 32 20Z"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <Button variant="outline" size="sm" className="w-full" disabled>
                <Camera className="mr-2 h-4 w-4" />
                Change Image
              </Button>
            </div> */}

            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <label htmlFor="toolName" className="text-sm font-medium">
                  {t('TOOL_NAME_LABEL')} <span className="text-destructive">*</span>
                </label>
                <Input
                  id="toolName"
                  placeholder={t('ENTER_NAME')}
                  {...register('toolName', {
                    required: t('TOOL_NAME_REQUIRED'),
                  })}
                  className={errors.toolName ? 'border-destructive' : ''}
                />
                {errors.toolName && (
                  <p className="text-sm text-destructive">{errors.toolName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  {t('DESCRIPTION')} <span className="text-destructive">*</span>
                </label>
                <Textarea
                  id="description"
                  placeholder={t('TOOL_DESCRIPTION_PLACEHOLDER')}
                  rows={4}
                  {...register('description', {
                    required: t('DESCRIPTION_IS_REQUIRED'),
                  })}
                  className={errors.description ? 'border-destructive' : ''}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>
              {isBlocksToolChecked && (
                <div className="pointer-events-none flex items-center gap-2">
                  <Controller
                    name="isOpenTool"
                    control={control}
                    render={({ field }) => (
                      <Checkbox id="editIsOpenTool" checked={!!field.value} disabled />
                    )}
                  />
                  <label
                    htmlFor="editIsOpenTool"
                    className="cursor-not-allowed text-sm font-medium text-muted-foreground"
                  >
                    {t('BLOCKS_TOOL_LABEL')}
                  </label>
                </div>
              )}

              {isAdmin && isBlocksToolChecked && !copiedBlocksTool && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pt-2">
                    <Controller
                      name="isBlocksAuthenticated"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="isBlocksAuthenticated"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    <label
                      htmlFor="isBlocksAuthenticated"
                      className="cursor-pointer text-sm font-medium"
                    >
                      {t('ALLOW_TOOL_AUTHENTICATION')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('ALLOW_AUTH_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="status"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                    <label htmlFor="status" className="text-sm font-medium">
                      {t('TOOL_VISIBILITY')}
                    </label>
                    <Tooltip>
                      <TooltipTrigger type="button" className="cursor-help">
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('TOGGLE_MCP_TOOL_STATUS_TOOLTIP')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t('CANCEL')}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={isPending || !isValid}>
            {isPending ? t('SAVING') : t('SAVE')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
